import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { useAuth } from '@/shared/contexts/AuthContext';
import { topicoFilaTela } from '@/shared/lib/canaisTela';
import { jukeboxRepository } from '../../data/jukeboxRepository';
import { timestampFimCooldown } from '../../domain/usecases/validarPedido';
import { mesclarFilaAposFetch } from '../../domain/usecases/mesclarFilaAposFetch';
import type { FilaJukebox } from '../../domain/entities/FilaJukebox';

/**
 * useFilaCooldown — fila ativa de pedidos e cooldown por vídeo.
 * O anon recebe a fila só no broadcast privado `fila` de `fila_tela_{telaId}`.
 * O CDC `jukebox-fila-cliente-{telaId}` abre só com role tenant ou super_admin.
 * Destroy/recreate no background fica no composer (`useJukebox`) pra ordenar com o `init`.
 */

const DEDUPE_EVENTO_MS = 10_000;

/** JSON com chaves ordenadas. A ordem das propriedades do payload não muda a versão. */
function canonico(valor: unknown): string {
  if (valor === undefined) return 'null';
  if (valor === null || typeof valor !== 'object') return JSON.stringify(valor) ?? 'null';
  if (Array.isArray(valor)) return `[${valor.map(canonico).join(',')}]`;
  const obj = valor as Record<string, unknown>;
  return `{${Object.keys(obj).sort().map((k) => `${JSON.stringify(k)}:${canonico(obj[k])}`).join(',')}}`;
}

function eventoRepetido(
  vistos: Map<string, number>,
  eventType: string,
  novo: unknown,
  old: unknown,
): boolean {
  const linha = eventType === 'DELETE' ? old : novo;
  if (!linha || typeof linha !== 'object') return false;
  const id = (linha as { id?: unknown }).id;
  if (typeof id !== 'string' || id.length === 0) return false;
  // CDC DELETE manda old só com a PK; o broadcast manda a linha inteira.
  const chave = eventType === 'DELETE' ? `DELETE:${id}` : `${eventType}:${canonico(linha)}`;
  const agoraMs = Date.now();
  for (const [k, t] of vistos) {
    if (agoraMs - t > DEDUPE_EVENTO_MS) vistos.delete(k);
  }
  const vistoEm = vistos.get(chave);
  if (vistoEm != null && agoraMs - vistoEm <= DEDUPE_EVENTO_MS) return true;
  vistos.set(chave, agoraMs);
  return false;
}

function lerEventoFila(payload: unknown): { operation: string; novo: unknown; old: unknown } | null {
  if (!payload || typeof payload !== 'object') return null;
  const corpo = payload as { operation?: unknown; new?: unknown; old?: unknown };
  const operation = corpo.operation;
  if (operation !== 'INSERT' && operation !== 'UPDATE' && operation !== 'DELETE') return null;
  const linha = operation === 'DELETE' ? corpo.old : corpo.new;
  if (!linha || typeof linha !== 'object') return null;
  const id = (linha as { id?: unknown }).id;
  if (typeof id !== 'string' || id.length === 0) return null;
  return { operation, novo: corpo.new, old: corpo.old };
}
export interface FilaCooldownState {
  filaAtiva: FilaJukebox[];
  setFilaAtiva: React.Dispatch<React.SetStateAction<FilaJukebox[]>>;
  cooldowns: Map<string, number>;
  setCooldowns: React.Dispatch<React.SetStateAction<Map<string, number>>>;
  agora: number;
  criarCanalFila(): void;
  destruirCanalFila(): void;
  /** Abre a janela de UM fetch e devolve o token dela. */
  iniciarJanelaFetch(): number;
  /** Mescla o snapshot com o que o CDC aplicou durante a janela `token` e a fecha. */
  aplicarSnapshotFila(snapshot: FilaJukebox[], token: number): void;
  /** Fecha a janela sem aplicar (fetch falhou). */
  cancelarJanelaFetch(token: number): void;
}

interface JanelaFetch {
  removidos: Set<string>;
  inseridos: Set<string>;
}

export function useFilaCooldown(
  telaId: string | undefined,
  /** Chamado para cada INSERT novo visto pelo CDC (ex.: placar da Batalha de Vibes na TV). */
  onInsert?: (item: FilaJukebox) => void,
): FilaCooldownState {
  const { role } = useAuth();
  const recebeCdc = role === 'tenant' || role === 'super_admin';
  const [filaAtiva, setFilaAtiva] = useState<FilaJukebox[]>([]);
  const [cooldowns, setCooldowns] = useState<Map<string, number>>(new Map());
  const [agora, setAgora] = useState(() => Date.now());

  const filaChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const filaBroadcastRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  // Mesmo motivo de useComandoChannel: não recriar o tópico enquanto o
  // unsubscribe anterior não tirou o canal da lista do client.
  const remocaoRef = useRef<Promise<unknown>>(Promise.resolve());
  const remocaoProntaRef = useRef(true);
  const geracaoRef = useRef(0);
  // Uma janela por fetch em andamento. Antes era uma janela única: um segundo
  // fetch simultâneo reaproveitava a janela, o primeiro snapshot a fechava, e
  // o segundo (mais velho) ressuscitava um item concluído ou sumia com um
  // INSERT que chegou entre os dois.
  const janelasRef = useRef<Map<number, JanelaFetch>>(new Map());
  const proximoTokenRef = useRef(0);
  const jaSubscreveuRef = useRef(false);
  // CDC e broadcast entregam o mesmo commit. A chave vive pouco tempo para
  // não engolir uma mudança posterior da mesma linha.
  const eventosVistosRef = useRef<Map<string, number>>(new Map());
  const onInsertRef = useRef(onInsert);
  useEffect(() => { onInsertRef.current = onInsert; });

  const registrarRemocao = (id: string) => {
    for (const janela of janelasRef.current.values()) janela.removidos.add(id);
  };

  const registrarInsercao = (id: string) => {
    for (const janela of janelasRef.current.values()) janela.inseridos.add(id);
  };

  const destruirCanalFila = useCallback(() => {
    geracaoRef.current++;
    const canais = [filaChannelRef.current, filaBroadcastRef.current].filter(
      (canal): canal is NonNullable<typeof canal> => canal != null,
    );
    filaChannelRef.current = null;
    filaBroadcastRef.current = null;
    if (canais.length === 0) return;
    remocaoProntaRef.current = false;
    const pendente = Promise.all(
      canais.map((canal) => Promise.resolve(supabase.removeChannel(canal)).catch(() => {})),
    );
    remocaoRef.current = pendente;
    void pendente.then(() => {
      if (remocaoRef.current === pendente) remocaoProntaRef.current = true;
    });
  }, []);

  const iniciarJanelaFetch = useCallback(() => {
    const token = ++proximoTokenRef.current;
    janelasRef.current.set(token, { removidos: new Set(), inseridos: new Set() });
    return token;
  }, []);

  const cancelarJanelaFetch = useCallback((token: number) => {
    janelasRef.current.delete(token);
  }, []);

  const aplicarSnapshotFila = useCallback((snapshot: FilaJukebox[], token: number) => {
    // Captura e fecha a janela fora do updater do setState (o React pode
    // chamar o updater duas vezes em StrictMode).
    const janela = janelasRef.current.get(token);
    janelasRef.current.delete(token);
    const removidos = janela?.removidos ?? new Set<string>();
    const inseridos = janela?.inseridos ?? new Set<string>();
    setFilaAtiva(prev => mesclarFilaAposFetch(prev, snapshot, removidos, inseridos));
  }, []);

  const criarCanalFila = useCallback(async () => {
    if (!telaId) return;
    destruirCanalFila();
    const geracao = geracaoRef.current;
    if (!remocaoProntaRef.current) await remocaoRef.current;
    if (geracao !== geracaoRef.current) return;
    jaSubscreveuRef.current = false;
    const aplicarEvento = (eventType: string, novo: unknown, old: unknown) => {
      if (eventoRepetido(eventosVistosRef.current, eventType, novo, old)) return;
      if (eventType === 'INSERT') {
        const inserido = novo as FilaJukebox;
        registrarInsercao(inserido.id);
        setFilaAtiva(prev => (
          prev.some(f => f.id === inserido.id) ? prev : [...prev, inserido]
        ));
        onInsertRef.current?.(inserido);
      } else if (eventType === 'UPDATE') {
        const updated = novo as FilaJukebox;
        if (updated.status === 'concluido') {
          registrarRemocao(updated.id);
          setFilaAtiva(prev => prev.filter(f => f.id !== updated.id));
          const fimCooldown = timestampFimCooldown(updated.concluido_em, Date.now());
          if (fimCooldown != null) {
            setCooldowns(prev => new Map(prev).set(updated.youtube_video_id, fimCooldown));
          }
        } else {
          setFilaAtiva(prev => prev.map(f => f.id === updated.id ? updated : f));
        }
      } else if (eventType === 'DELETE') {
        registrarRemocao((old as FilaJukebox).id);
        setFilaAtiva(prev => prev.filter(f => f.id !== (old as FilaJukebox).id));
      }
    };
    const acompanharRejoin = (status: string) => {
      if (status !== 'SUBSCRIBED') return;
      // supabase-js reassocia o canal sozinho quando o socket cai e volta
      // (desconexão silenciosa em background — ver heartbeatCallback em
      // shared/lib/supabase.ts), sem passar pelo destroy/create do composer
      // (visibilitychange/pageshow/focus/online). Sem este catch-up, os
      // eventos perdidos durante a queda nunca são repostos e a fila trava
      // até um reload manual.
      if (!jaSubscreveuRef.current) {
        jaSubscreveuRef.current = true;
        return;
      }
      const token = iniciarJanelaFetch();
      jukeboxRepository.listarAtivos(telaId)
        .then(fila => aplicarSnapshotFila(fila, token))
        .catch(() => cancelarJanelaFetch(token));
    };
    if (recebeCdc) {
      filaChannelRef.current = supabase
        .channel(`jukebox-fila-cliente-${telaId}`)
        .on('postgres_changes', {
          event:  '*',
          schema: 'public',
          table:  'fila_jukebox',
          filter: `tela_id=eq.${telaId}`,
        }, ({ eventType, new: novo, old }) => {
          aplicarEvento(eventType, novo, old);
        })
        .subscribe(acompanharRejoin);
    }
    // Com CDC, o catch-up fica só nele para os dois canais não buscarem juntos.
    filaBroadcastRef.current = supabase
      .channel(topicoFilaTela(telaId), { config: { private: true } })
      .on('broadcast', { event: 'fila' }, ({ payload }) => {
        const evento = lerEventoFila(payload);
        if (!evento) return;
        aplicarEvento(evento.operation, evento.novo, evento.old);
      })
      .subscribe(recebeCdc ? undefined : acompanharRejoin);
  }, [telaId, recebeCdc, destruirCanalFila, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch]);

  useEffect(() => {
    void criarCanalFila();
    return destruirCanalFila;
  }, [criarCanalFila, destruirCanalFila]);

  useEffect(() => {
    if (cooldowns.size === 0) return;
    const agr = Date.now();
    const proxExpiracao = Math.min(
      ...Array.from(cooldowns.values()).map(disponivelEm => disponivelEm - agr),
    );
    if (proxExpiracao <= 0) return;
    const id = setTimeout(() => setAgora(Date.now()), proxExpiracao + 100);
    return () => clearTimeout(id);
  }, [cooldowns, agora]);

  useEffect(() => {
    if (cooldowns.size === 0) return;
    const id = setInterval(() => setAgora(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [cooldowns.size]);

  return {
    filaAtiva, setFilaAtiva, cooldowns, setCooldowns, agora,
    criarCanalFila, destruirCanalFila, iniciarJanelaFetch, aplicarSnapshotFila, cancelarJanelaFetch,
  };
}
